import { useId, useState } from 'react';
import type { GameOverlay, Layout } from '@replay/shared';
import { useOverlayChoice } from '../../player/overlay/useOverlays.ts';
import '../../styles/player.css';

type TemplateOverlay = GameOverlay & { templateId?: string; templateName?: string };
type Orientation = 'landscape' | 'portrait';
export interface ThemeEntry {
  overlay: TemplateOverlay;
  layout: Layout;
  /** Shown under the name when another theme has the same name. */
  detail: string | null;
}

/**
 * Themes by screen orientation (a theme with both appears in both sections),
 * so every section has tiles of one shape.
 */
export function groupByOrientation(overlays: TemplateOverlay[]): { orientation: Orientation; themes: ThemeEntry[] }[] {
  const names = new Map<string, number>();
  for (const o of overlays) names.set(o.name, (names.get(o.name) ?? 0) + 1);
  return (['landscape', 'portrait'] as const)
    .map((orientation) => ({
      orientation,
      themes: overlays
        .filter((o) => o.layouts[orientation])
        .map((o) => ({ overlay: o, layout: o.layouts[orientation]!, detail: (names.get(o.name) ?? 0) > 1 ? (o.templateName ?? null) : null })),
    }))
    .filter((g) => g.themes.length);
}

/** How a theme looks while playing: its art with the game screen in place. */
function ThemePreview({ layout, screenshot }: { layout: Layout; screenshot?: string }) {
  const r = layout.screen;
  return (
    <span className="theme-preview" style={{ aspectRatio: String(layout.aspect) }} aria-hidden="true">
      {layout.backgroundUrl && <img className="theme-preview-art" src={layout.backgroundUrl} alt="" loading="lazy" />}
      <span className="theme-preview-screen" style={{ left: `${r.x * 100}%`, top: `${r.y * 100}%`, width: `${r.w * 100}%`, height: `${r.h * 100}%` }}>
        {screenshot && <img src={screenshot} alt="" loading="lazy" />}
      </span>
      {layout.frameUrl && <img className="theme-preview-art" src={layout.frameUrl} alt="" loading="lazy" />}
    </span>
  );
}

export function OverlayPicker({ gameId, screenshot }: { gameId: string; screenshot?: string }) {
  const headingId = useId();
  const { overlays, choice, setChoice } = useOverlayChoice(gameId),
    [error, setError] = useState('');
  const pick = (value: string | null) => {
    setError('');
    void setChoice(value).catch((e) => setError(e instanceof Error ? e.message : 'No se pudo guardar'));
  };
  return (
    <div className="overlay-picker">
      <div className="overlay-picker-chips" data-nav-row role="group" aria-label="Apariencia general">
        <button className="chip" aria-pressed={choice === null} onClick={() => pick(null)}>
          Predeterminado
        </button>
        <button className="chip" aria-pressed={choice === 'minimal'} onClick={() => pick('minimal')}>
          Minimalista
        </button>
        <button className="chip" aria-pressed={choice === 'none'} onClick={() => pick('none')}>
          Sin tema
        </button>
      </div>
      {groupByOrientation(overlays).map((group) => (
        <div key={group.orientation} className="overlay-picker-group" role="group" aria-labelledby={`${headingId}-${group.orientation}`}>
          <h3 id={`${headingId}-${group.orientation}`} className="overlay-picker-heading">
            {group.orientation === 'landscape' ? 'Pantalla horizontal' : 'Pantalla vertical (móvil)'}
          </h3>
          <div className={`theme-grid ${group.orientation}`} data-nav-row>
            {group.themes.map(({ overlay: o, layout, detail }) => (
              <button key={o.id} className="theme-tile" aria-pressed={choice === o.id} onClick={() => pick(o.id)}>
                <ThemePreview layout={layout} screenshot={screenshot} />
                <span className="theme-tile-name">{o.name}</span>
                {detail && <span className="theme-tile-detail">{detail}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
      {error && (
        <p role="alert" className="error-box">
          {error}
        </p>
      )}
    </div>
  );
}
